import { useState } from "react"
import { useForm } from "@inertiajs/react"
import { Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"

export function UploadTranscriptDialog({ open, onOpenChange }) {
  const [clientErrors, setClientErrors] = useState({})

  const form = useForm({
    transcript: "",
    interviewee_name: "",
    interviewee_job_title: "",
    interviewee_company: "",
    interviewee_company_website: "",
    key_focus_areas: "",
    source: "manual_upload",
  })

  const validate = () => {
    const errors = {}
    if (!form.data.transcript.trim()) {
      errors.transcript = "Transcript is required"
    }
    if (!form.data.interviewee_name.trim()) {
      errors.interviewee_name = "Interviewee name is required for manual uploads"
    }
    setClientErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    form.post("/app/coach/meetings", {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setClientErrors({})
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const ext = file.name.split(".").pop()?.toLowerCase()

    try {
      let text = ""

      if (ext === "docx") {
        const mammoth = await import("mammoth")
        const arrayBuffer = await file.arrayBuffer()
        const result = await mammoth.extractRawText({ arrayBuffer })
        text = result.value
      } else if (ext === "vtt") {
        const raw = await file.text()
        text = raw
          .split("\n")
          .filter((line) => {
            if (line.trim() === "WEBVTT") return false
            if (/^\d{1,2}:\d{2}[:.]\d{3}\s+-->\s+/.test(line)) return false
            if (/^NOTE\b/.test(line)) return false
            return true
          })
          .join("\n")
          .replace(/\n{3,}/g, "\n\n")
          .trim()
      } else {
        // .txt, .md — plain text
        text = await file.text()
      }

      form.setData("transcript", text)
    } catch (err) {
      console.error("Failed to read file:", err)
    }

    e.target.value = ""
  }

  const errorFor = (field) => clientErrors[field] || form.errors?.[`meeting.${field}`]

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Upload Transcript
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Upload a transcript file or paste text to generate an article draft.
              Supported formats: .txt, .md, .vtt, .docx. Have a PDF? Open it and copy-paste the text, or export from your meeting platform as a .txt file.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="transcript">Transcript *</Label>
                <label className="text-xs text-kf-red hover:underline cursor-pointer">
                  <Upload className="h-3 w-3 inline mr-1" />
                  Upload file
                  <input
                    type="file"
                    accept=".txt,.md,.vtt,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <Textarea
                id="transcript"
                value={form.data.transcript}
                onChange={(e) => form.setData("transcript", e.target.value)}
                placeholder="Paste the full meeting transcript here..."
                className="max-h-48 resize-y"
                rows={6}
              />
              {form.data.transcript && (
                <p className="text-xs text-zinc-500">
                  {form.data.transcript.split(/\s+/).length.toLocaleString()} words
                </p>
              )}
              {errorFor("transcript") && (
                <p className="text-sm text-red-600">{errorFor("transcript")}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload_interviewee_name">Interviewee Name *</Label>
              <Input
                id="upload_interviewee_name"
                value={form.data.interviewee_name}
                onChange={(e) => form.setData("interviewee_name", e.target.value)}
                placeholder="John Smith"
              />
              {errorFor("interviewee_name") && (
                <p className="text-sm text-red-600">{errorFor("interviewee_name")}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="upload_job_title">Job Title</Label>
                <Input
                  id="upload_job_title"
                  value={form.data.interviewee_job_title}
                  onChange={(e) => form.setData("interviewee_job_title", e.target.value)}
                  placeholder="CEO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload_company">Company</Label>
                <Input
                  id="upload_company"
                  value={form.data.interviewee_company}
                  onChange={(e) => form.setData("interviewee_company", e.target.value)}
                  placeholder="Acme Inc."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload_website">Company Website</Label>
              <Input
                id="upload_website"
                value={form.data.interviewee_company_website}
                onChange={(e) => form.setData("interviewee_company_website", e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload_focus_areas">Key Areas to Focus On</Label>
              <Textarea
                id="upload_focus_areas"
                value={form.data.key_focus_areas}
                onChange={(e) => form.setData("key_focus_areas", e.target.value)}
                placeholder="Specific topics or themes for the article..."
                rows={3}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Uploading..." : "Generate Article"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
